import { Box, Stack, Tooltip, Typography } from "@mui/material";

import { ConversationInfo, ConversationUserStatus, ConversationType, dateSimplifier, determineMe, timeSimplifier } from "../../redux/conversation/conversationSlice";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getNamedObject, getNamedObjectBy, ObjectRetrieveRequest, retrieveObject, selectObjectCache } from "../../redux/object/objectCacheSlice";
import { NamedObject } from "../../redux/object/namedObject";
import { getObjectTargetName, ObjectTarget } from "../../app/permissions";
import BadgedAvatar from "../base/BadgedAvatar";
import { SelectedConversation, selectedConversationSlice } from "../../redux/navigation/selectedConversationSlice";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";

import { selectUser } from "../../redux/user/userSlice";

interface ConversationTileProps {
    // me: NamedObject;
    conversation: ConversationInfo;
}

export const ConversationTile = (props: ConversationTileProps) => {
    const dispatch = useAppDispatch();
    const conversation = props.conversation;
    const objectCache = useAppSelector(selectObjectCache);
    const user = useAppSelector(selectUser);

    const [title, setTitle] = useState<string>(conversation.id);
    const [avatar, setAvatar] = useState<string | undefined>(undefined);
    const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
    const [subavatar, setSubavatar] = useState<string | undefined>(undefined);
    
    const myParty = determineMe(conversation, user.priza.info);
    const me: NamedObject = getNamedObjectBy(objectCache, myParty?.objectTarget, myParty?.targetId)  || NamedObject.unknown();

    useEffect(() => {
        setTitle(conversation.id);
        setAvatar(undefined);
        setSubtitle(undefined);
        setSubavatar(undefined);
        if(conversation.conversationType == ConversationType.SINGLE_PARTY) {
            if(conversation.parties[0]?.objectTarget == ObjectTarget.STUDENT) {
                setSubtitle("Famĩlia");
            } else {
                setSubtitle( getObjectTargetName( conversation.parties[0]?.objectTarget ) );
            }
            if(conversation.parties.length > 0) {
                const party = conversation.parties[0];
                const request = {
                    objectTarget: party.objectTarget,
                    targetId: party.targetId,
                } as ObjectRetrieveRequest;
                const named = getNamedObject( objectCache, request );
                if(named) {
                    setTitle(named.display);
                    setAvatar(named.thumbnail);
                } else {
                    dispatch(retrieveObject(request));
                }
            }
        } else if(conversation.conversationType == ConversationType.DUAL_PARTY) {
            // console.log(`Dual party conversation ${JSON.stringify(me)}`);
            setSubtitle(me.display + " (Privado)");
            setSubavatar(me.thumbnail);
            for(const party of conversation.parties) {  
                if(! (party.objectTarget == me.type 
                        && party.targetId == me.targetId) ) {
                    const request: ObjectRetrieveRequest = {
                        objectTarget: party.objectTarget,
                        targetId: party.targetId,
                    };
                    // console.log("Dual party request", request);
                    const named = getNamedObject( objectCache, request );
                    // console.log("Dual party named", named?.display, named);
                    if(named) {
                        setTitle(named.display);
                        setAvatar(named.thumbnail);
                    } else {
                        dispatch(retrieveObject(request));
                    }
                }
            }            
        } else if(conversation.conversationType == ConversationType.ENTITY_GROUP) {
            // console.log("Entity group party conversation");
            setTitle(`${conversation.key}`);
            const pieces = conversation.key?.split(":");
            if(pieces && pieces.length > 1) {
                const objectTarget = parseInt( pieces[0] );
                setSubtitle("(Grupo) " + getObjectTargetName(objectTarget));
                const targetId = pieces[1];
                const request: ObjectRetrieveRequest = { objectTarget, targetId };
                const named = getNamedObject(objectCache, request);
                // console.log("Entity group party named", named);
                if(named) {
                    setTitle(named.display);
                    setAvatar(named.thumbnail);
                } else {
                    dispatch(retrieveObject(request));
                }
                // console.log(`Entity group party ${named?.display} and ${named?.thumbnail}`);
            }
        } else if(conversation.conversationType == ConversationType.GENERAL_GROUP) {
            console.log("General group not implemented yet");
        } else {
            console.warn("Unknown conversation type", conversation.conversationType);
        }
    }, [conversation, objectCache]);

    // console.log(`Title is ${title} and avatar is ${avatar}`);

    const message = conversation.lastMessageDate && conversation.lastMessageDate.length > 0
        ? dateSimplifier( conversation.lastMessageDate ) + " " + timeSimplifier( conversation.lastMessageDate )
        : "Sem mensagens";

    const icons: React.ReactNode[] = [];
    if((conversation.lastNonSystemId || conversation.lastMessageId || 0) > (conversation.lastReadMessageId || 0)) {
        icons.push(
            <Tooltip key="new-messages" title="Novas mensagens">
                <MarkEmailUnreadIcon key="unread" 
                    sx={{ color: conversation.conversationUserStatus == ConversationUserStatus.SILENT ? "#777" : "green", fontSize: 20, width: "32px" }} />
            </Tooltip>
        );
    }
    if(conversation.conversationUserStatus == ConversationUserStatus.SILENT) {
        icons.push(
            <Tooltip key="silenced" title="Conversa silenciada">
                <NotificationsOffIcon key="silent" sx={{ color: "#777", fontSize: 20, width: "32px" }} />
            </Tooltip>
        );
    }

    let invert = false;
    // let color = "white";
    // if(me.type == ObjectTarget.SCHOOL) {
    //     color = "#CFF";
    // } else if(me.type == ObjectTarget.CLASSROOM) {
    //     color = "#CCC";
    // }
    let align = "left";
    if( conversation.conversationType == ConversationType.ENTITY_GROUP ) {
        align = "right";
        invert = true;
    }

    const avatarSize = 70;
    // , pl: invert ? 4 : 0
    const avatarEl = <Box sx={{ width: avatarSize, height: avatarSize, 
            alignContent: "center", justifyContent: "center", 
            justifyItems: "center", mx: 1}}>
        <BadgedAvatar 
            title={title} 
            avatar={avatar} 
            subtitle={subtitle} 
            subavatar={subavatar}
            size={`${avatarSize}px`}
            subsize='37px'
            marginRight={invert ? 0 : 2}
        />
    </Box>;

    const iconsEl = <Stack direction="column" spacing={0} sx={{ width: "50px", border: "1px solid transparent", textAlign: align }}>
        {icons}
        <Typography variant="body2" sx={{ textWrap: "nowrap" }}>&nbsp;</Typography>
    </Stack>;


    const subtileElement = subtitle ? (
        <Typography variant="caption" sx={{ textWrap: "nowrap", textOverflow: "ellipsis", overflow: "hidden", textAlign: align }}>
            {subtitle}
        </Typography>
    ) : undefined;

    return <Box 
        // sx={{backgroundColor: color}}
        onClick={() => { 
            dispatch(selectedConversationSlice.actions.selectConversation(
                {
                    meObjectTarget: me.type,
                    meTargetId: me.targetId,
                    conversationId: props.conversation.id,
                    title: title,
                    avatar: avatar,
                    subtitle: subtitle,
                    subavatar: subavatar,
                } as SelectedConversation
            ));
        }}>
        <Stack direction="row" spacing={0} sx={{ mx: 2, paddingY: 1, borderBottom: "1px solid gray" }}>
            {/* { invert && iconsEl } */}
            { !invert && avatarEl}
            <Stack direction="column" spacing={0} sx={{ marginLeft: 0, overflow: "auto", width: "100%" }}>
                {subtileElement}
                <Typography variant="h6" sx={{ width: "100%", textWrap: "nowrap", textOverflow: "ellipsis", overflow: "hidden", textAlign: align }}>
                    {title}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: 1, fontSize: 10,  textWrap: "nowrap", textOverflow: "ellipsis", overflow: "hidden", textAlign: align }}>
                    {message}
                </Typography>
            </Stack>
            { invert && avatarEl}
            { iconsEl }
        </Stack>
    </Box>;

};